import type { JSONContent } from "@tiptap/core";
import type { Editor } from "@tiptap/react";

export interface QueryBarSkillAttrs {
  id: string;
  name: string;
  slug: string;
}

export interface QueryBarSkillRange {
  start: number;
  end: number;
  skill?: QueryBarSkillAttrs;
}

function slashToken(slug: unknown): string {
  return typeof slug === "string" && slug.length > 0 ? `/${slug}` : "";
}

function skillAttrsFromNode(node: JSONContent): QueryBarSkillAttrs {
  const slug = typeof node.attrs?.slug === "string" ? node.attrs.slug : "";
  return {
    id: typeof node.attrs?.id === "string" ? node.attrs.id : slug,
    name: typeof node.attrs?.name === "string" ? node.attrs.name : slug,
    slug,
  };
}

function textToInlineContent(text: string): JSONContent[] {
  if (!text) return [];
  const lines = text.split("\n");
  const content: JSONContent[] = [];
  lines.forEach((line, index) => {
    if (line) content.push({ type: "text", text: line });
    if (index < lines.length - 1) content.push({ type: "hardBreak" });
  });
  return content;
}

function walkInline(
  node: JSONContent,
  visit: {
    text: (value: string) => void;
    hardBreak: () => void;
    skillMention: (skill: QueryBarSkillAttrs) => void;
  }
): void {
  if (node.type === "text") {
    visit.text(node.text ?? "");
    return;
  }
  if (node.type === "hardBreak") {
    visit.hardBreak();
    return;
  }
  if (node.type === "skillMention") {
    visit.skillMention(skillAttrsFromNode(node));
    return;
  }
  node.content?.forEach((child) => walkInline(child, visit));
}

/** Flatten the editor doc back to the submit string: chips become `/${slug}`. */
export function serializeQueryBarDoc(doc: JSONContent): string {
  const parts: string[] = [];
  walkInline(doc, {
    text: (value) => parts.push(value),
    hardBreak: () => parts.push("\n"),
    skillMention: (skill) => parts.push(slashToken(skill.slug)),
  });
  return parts.join("");
}

/** Rebuild a TipTap doc, turning `highlightRanges` into atomic skill chips. */
export function promptToEditorContent(
  text: string,
  ranges: readonly QueryBarSkillRange[] = []
): JSONContent {
  const content: JSONContent[] = [];
  let cursor = 0;
  const sorted = [...ranges]
    .filter((range) => range.end > range.start)
    .sort((left, right) => left.start - right.start);

  for (const range of sorted) {
    const start = Math.max(cursor, Math.min(range.start, text.length));
    const end = Math.max(start, Math.min(range.end, text.length));
    if (start > cursor) {
      content.push(...textToInlineContent(text.slice(cursor, start)));
    }
    if (end > start) {
      const token = text.slice(start, end);
      const slug = range.skill?.slug ?? (token.startsWith("/") ? token.slice(1) : token);
      content.push({
        type: "skillMention",
        attrs: {
          id: range.skill?.id ?? slug,
          name: range.skill?.name ?? slug,
          slug,
        },
      });
    }
    cursor = end;
  }
  if (cursor < text.length) {
    content.push(...textToInlineContent(text.slice(cursor)));
  }

  return {
    type: "doc",
    content: [{ type: "paragraph", content: content.length > 0 ? content : [] }],
  };
}

function collectSkillMentionRanges(doc: JSONContent): QueryBarSkillRange[] {
  const ranges: QueryBarSkillRange[] = [];
  let offset = 0;
  walkInline(doc, {
    text: (value) => {
      offset += value.length;
    },
    hardBreak: () => {
      offset += 1;
    },
    skillMention: (skill) => {
      const token = slashToken(skill.slug);
      const start = offset;
      offset += token.length;
      ranges.push({ start, end: offset, skill });
    },
  });
  return ranges;
}

export function skillRangesMatch(doc: JSONContent, ranges: readonly QueryBarSkillRange[]): boolean {
  const current = collectSkillMentionRanges(doc);
  if (current.length !== ranges.length) return false;
  return current.every(
    (range, index) => range.start === ranges[index].start && range.end === ranges[index].end
  );
}

/**
 * Slash-menu and `replaceRange` speak in serialized-string offsets (`"/dcf now"`).
 * ProseMirror treats a skill chip as one atom, so these convert between the two.
 */
export function editorSelectionToStringOffsets(editor: Editor): {
  start: number;
  end: number;
} {
  const { from, to } = editor.state.selection;
  return {
    start: pmPosToStringOffset(editor, from),
    end: pmPosToStringOffset(editor, to),
  };
}

export function stringOffsetToPmPos(editor: Editor, offset: number): number {
  const doc = editor.state.doc;
  let remaining = Math.max(0, offset);
  let mapped = 1;

  doc.descendants((node, pos) => {
    if (remaining < 0) return false;
    if (node.isText) {
      const length = node.text?.length ?? 0;
      if (remaining <= length) {
        mapped = pos + remaining;
        remaining = -1;
        return false;
      }
      remaining -= length;
      mapped = pos + length;
      return true;
    }
    if (node.type.name === "hardBreak") {
      if (remaining === 0) {
        mapped = pos;
        remaining = -1;
        return false;
      }
      remaining -= 1;
      mapped = pos + node.nodeSize;
      return true;
    }
    if (node.type.name === "skillMention") {
      const length = slashToken(node.attrs.slug).length;
      if (remaining < length) {
        mapped = remaining === 0 ? pos : pos + node.nodeSize;
        remaining = -1;
        return false;
      }
      remaining -= length;
      mapped = pos + node.nodeSize;
    }
    return true;
  });

  return Math.max(1, Math.min(mapped, doc.content.size));
}

export function pmPosToStringOffset(editor: Editor, pmPos: number): number {
  let offset = 0;
  editor.state.doc.nodesBetween(0, pmPos, (node, pos) => {
    if (pos >= pmPos) return false;
    if (node.isText) {
      offset += Math.min(node.text?.length ?? 0, Math.max(0, pmPos - pos));
      return true;
    }
    if (node.type.name === "hardBreak" && pos + node.nodeSize <= pmPos) {
      offset += 1;
      return true;
    }
    if (node.type.name === "skillMention" && pos + node.nodeSize <= pmPos) {
      offset += slashToken(node.attrs.slug).length;
    }
    return true;
  });
  return offset;
}
